/**
 * Shared helpers for the MCP Server Builder screens.
 *
 * Kept free of JSX so the list, the detail shell and every tab can pull from
 * the same vocabulary — status tones, the tool-count threshold, JSON-Schema →
 * form-field mapping, and install-snippet templating.
 */

// ── Response shapes ────────────────────────────────────────────────────────

/**
 * List endpoints across this API vary between a bare array and an envelope
 * (`{servers: []}`, `{tools: []}`, `{logs: []}`), so every caller unwraps
 * through here instead of guessing.
 */
export const unwrapList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [...keys, 'items', 'results', 'data'];
  for (const key of candidates) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
};

// ── Server types ───────────────────────────────────────────────────────────
// Presented as three separate entry points rather than a dropdown, because the
// follow-on steps differ completely: `api` imports a spec, the other two have
// their tools created for them.

export const SERVER_TYPES = {
  api: {
    value: 'api',
    label: 'From an API',
    short: 'API',
    description: 'Import an OpenAPI, Swagger, Postman or cURL spec.',
    needsImport: true,
  },
  agent: {
    value: 'agent',
    label: 'From an agent',
    short: 'Agent',
    description: 'Expose one of your agents as a single tool.',
    needsImport: false,
  },
  knowledge: {
    value: 'knowledge',
    label: 'From knowledge',
    short: 'Knowledge',
    description: 'Expose knowledge base folders as `search` and `fetch`.',
    needsImport: false,
  },
};

export const getServerTypeMeta = (type) => SERVER_TYPES[type] || SERVER_TYPES.api;

/** `api` servers are the only ones the customer imports a spec for. */
export const serverNeedsImport = (server) => getServerTypeMeta(server?.server_type).needsImport;

// ── Credential mode ────────────────────────────────────────────────────────
// Who supplies the key that reaches the upstream API. `shared` is the original
// behaviour and stays the default; `per_user` stores no key at all — each end
// user puts their own into their own client, and only ever reaches their own
// data. There is deliberately no fallback: a missing user key fails loudly
// rather than quietly borrowing the owner's, which would show one person
// another person's data. `per_user_oauth` reaches the same place without
// anybody pasting anything — the end user signs in with the customer's own
// identity provider.

export const OAUTH_CREDENTIAL_MODE = 'per_user_oauth';

/**
 * The `inbound_auth_mode` that must accompany OAuth credential mode. The API
 * refuses to publish when the two disagree, so the UI always sends them
 * together and never exposes them as independent choices.
 */
export const OAUTH_INBOUND_MODE = 'oauth';

export const CREDENTIAL_MODES = {
  shared: {
    value: 'shared',
    label: 'I provide one key for everyone',
    badge: 'Shared key',
    helper: 'Everyone using this MCP acts as the same account.',
  },
  per_user: {
    value: 'per_user',
    label: 'Each user provides their own',
    badge: 'Per-user keys',
    helper: 'Each person enters their own key and sees only their own data.',
  },
  // A variant of per_user, not a third choice: the key travels in the URL for
  // clients that can't send custom headers. Surfaced as a checkbox under
  // per-user, never as its own dropdown option.
  per_user_url: {
    value: 'per_user_url',
    label: 'Each user provides their own',
    badge: 'Per-user keys (URL)',
    helper: 'Each person enters their own key and sees only their own data.',
  },
  // Sign-in instead of a pasted key. The end user logs in on the customer's
  // own site — they never see a Fouray page — and the resulting token both
  // identifies them and authorises the call, which is why it replaces the
  // inbound-auth choice rather than sitting alongside it.
  [OAUTH_CREDENTIAL_MODE]: {
    value: OAUTH_CREDENTIAL_MODE,
    label: 'Users sign in with your own login',
    badge: 'Sign-in (OAuth)',
    helper:
      'Each person signs in with your own login system, on your own site. Nobody pastes a key, and nobody sees a Fouray page.',
  },
};

/** The three choices the dropdown offers. URL mode is a checkbox under per-user. */
export const CREDENTIAL_MODE_OPTIONS = [
  CREDENTIAL_MODES.shared,
  CREDENTIAL_MODES.per_user,
  CREDENTIAL_MODES[OAUTH_CREDENTIAL_MODE],
];

const NON_SHARED_MODES = ['per_user', 'per_user_url', OAUTH_CREDENTIAL_MODE];

export const getCredentialMode = (server) => {
  const mode = server?.credential_mode;
  return NON_SHARED_MODES.includes(mode) ? mode : 'shared';
};

/**
 * True whenever the credential does not come from the owner — both per-user
 * variants and OAuth sign-in. What they share is that nothing is stored here,
 * so it also answers "does this server need a credential supplied per call".
 */
export const isPerUser = (server) => getCredentialMode(server) !== 'shared';

export const isPerUserUrl = (server) => getCredentialMode(server) === 'per_user_url';

export const isOauthMode = (mode) => mode === OAUTH_CREDENTIAL_MODE;

export const isOauthServer = (server) => isOauthMode(getCredentialMode(server));

/**
 * Collapses the stored value to the option the dropdown binds to: the two
 * per-user variants share one option (URL is a checkbox beneath it), while
 * OAuth is an option in its own right.
 */
export const toBaseCredentialMode = (mode) => {
  if (isOauthMode(mode)) return OAUTH_CREDENTIAL_MODE;
  return mode === 'per_user' || mode === 'per_user_url' ? 'per_user' : 'shared';
};

/** No upstream key is stored on the server in any non-shared mode. */
export const storesNoUpstreamKey = (mode) => toBaseCredentialMode(mode) !== 'shared';

export const allowsUrlKey = (mode) => mode === 'per_user_url';

export const withUrlKey = (allow) => (allow ? 'per_user_url' : 'per_user');

// ── Upstream error messages ────────────────────────────────────────────────
// `expose_upstream_errors` passes the API's own 4xx reason to the AI so it can
// fix the call or ask the user. It only takes effect when each user brings
// their own key: on a shared key the caller is a stranger to the owner's API,
// so the API ignores it — the switch is shown but disabled there.

export const EXPOSE_ERRORS_COPY = {
  label: "Show the AI my API's error messages",
  tooltip:
    'Tells the AI why your API said no, so it can fix the call itself. Example: instead of "request rejected (400)", the AI sees "folder_ids: Field required" and adds the missing folder. Server errors (5xx) and secrets are never shown.',
  helperOn: 'On: the AI sees why a call was rejected, e.g. "folder_ids: Field required".',
  helperOff: 'Off: the AI only sees "The API rejected the request (HTTP 400)".',
  always: 'Server errors and secrets always stay hidden.',
  sharedOnly:
    'Only works when each user brings their own key. With one shared key this setting is ignored.',
};

// Server-level `instructions`: sent to the calling model (Claude, Town…) when
// it connects, before any tool is called.
export const SERVER_INSTRUCTIONS_COPY = {
  label: 'Instructions for AI apps (optional)',
  tooltip:
    'Guidance Claude, Town or another AI app reads as soon as it connects, before it uses any tool. Example: "Always ask the user before deleting anything."',
  helper: 'Changes reach connected apps within a few minutes — no need to publish again.',
  placeholder: 'e.g. Always ask the user before deleting anything.',
};

export const IMPORT_MODE_COPY = {
  sectionTip:
    'Choose what happens to the tools you already have when you import a new version of the spec.',
  sync: {
    label: 'Sync with the new spec (recommended)',
    hint: 'Updates your tools and keeps your changes.',
    tooltip:
      'Best for an updated spec. Your names, descriptions and on/off choices stay. Example: you renamed "get_users" to "list_customers" — it keeps that name and only gets the new inputs. New endpoints are added; removed ones are turned off, not deleted.',
  },
  append: {
    label: 'Add alongside the current tools',
    hint: 'Keeps your tools and adds the spec’s tools next to them.',
    tooltip:
      'Nothing is changed or removed. If a name already exists, the new tool gets "_2". Example: "get_users" stays, and the new one is "get_users_2".',
  },
  replace: {
    label: 'Replace the current tools',
    hint: 'Deletes your tools and starts fresh.',
    tooltip:
      'Removes every current tool, including your renames, edited descriptions and on/off choices, then imports the spec from scratch. Use it only to start over.',
  },
};

export const INVALID_SCHEMA_COPY = {
  badge: 'Invalid schema',
  badgeTooltip: (reason) =>
    `Some AI apps (such as ChatGPT) will refuse this tool because its input description is broken. Reason: ${reason}. Click the pencil to fix it, or turn the tool off.`,
  openTooltip: 'Open this tool and fix its input schema.',
  disableTooltip:
    'Turn this tool off so AI apps stop seeing it. Publishing works again, and you can fix it and turn it back on later.',
  reimportTooltip:
    'Use this if you already fixed the spec file. Example: your OpenAPI file now lists the inputs correctly. Your names and on/off choices are kept.',
  listHint: 'Click a name to fix its schema, or turn the tool off.',
  schemaTooltip:
    'The inputs this tool accepts, written as JSON Schema. The top level must be an object. Example: {"type": "object", "properties": {"city": {"type": "string"}}}',
  drawerAlert: (reason) =>
    `Some AI apps (such as ChatGPT) refuse this tool: ${reason}. Fix the schema below, or turn the tool off.`,
};

/** Header an end user puts their own key into, when the server doesn't say. */
export const DEFAULT_USER_KEY_HEADER = 'X-Upstream-Authorization';

/**
 * Per-user and sign-in modes both carry exactly one secret per request, so auth
 * types needing several at once (the OAuth flows, NetSuite TBA) can't be
 * expressed — and NO_AUTH has no key for the user to supply in the first place.
 */
export const PER_USER_AUTH_TYPES = ['API_KEY', 'BEARER_TOKEN', 'BASIC_AUTH'];

export const isAuthTypeAllowedPerUser = (value) => PER_USER_AUTH_TYPES.includes(value);

export const PER_USER_AUTH_DISABLED_HINT =
  'Not available when each user brings their own credential — these need several secrets at once.';

/** Upstream auth types offered by the builder. */
export const UPSTREAM_AUTH_TYPES = [
  { value: 'NO_AUTH', label: 'No auth' },
  { value: 'API_KEY', label: 'API Key' },
  { value: 'BEARER_TOKEN', label: 'Bearer Token' },
  { value: 'BASIC_AUTH', label: 'Basic Auth' },
];

/** Label for the secret field, which differs per auth type. */
export const getSecretLabel = (authType) => {
  if (authType === 'BEARER_TOKEN') return 'Token';
  if (authType === 'BASIC_AUTH') return 'username:password';
  return 'API key / secret';
};

// ── OAuth config ───────────────────────────────────────────────────────────
// Sent as a nested `oauth_config` object on create and PATCH. The customer is
// copying these values out of their own identity provider's dashboard, so the
// copy names the thing they are looking for rather than the spec term.

/** Scopes are edited as one free-text field and sent as a list. */
export const parseScopes = (value) =>
  String(value || '')
    .split(/[\s,]+/)
    .filter(Boolean);

export const formatScopes = (value) => (Array.isArray(value) ? value.join(', ') : value || '');

/** The API rejects any non-https URL in this block outright. */
export const isHttpsUrl = (value) => /^https:\/\/\S+$/i.test(String(value || '').trim());

export const emptyOauthForm = () => ({
  issuer: '',
  audience: '',
  jwks_uri: '',
  required_scopes: '',
  introspection_endpoint: '',
  client_id: '',
  client_secret: '',
});

export const oauthConfigToForm = (config) => ({
  ...emptyOauthForm(),
  issuer: config?.issuer || '',
  audience: config?.audience || '',
  jwks_uri: config?.jwks_uri || '',
  required_scopes: formatScopes(config?.required_scopes),
  introspection_endpoint: config?.introspection_endpoint || '',
  client_id: config?.client_id || '',
  // Arrives masked (`****1234`) like every other stored secret.
  client_secret: config?.client_secret || '',
});

/**
 * Nearly always the issuer plus this path — offered rather than assumed, and
 * ONLY for a bare host. Appending it to an issuer that already carries a path
 * builds things like `…/oauth/authorize/.well-known/jwks.json`, which 404s;
 * returning empty leaves whatever is in the field alone instead.
 */
export const suggestJwksUri = (issuer) => {
  const base = String(issuer || '')
    .trim()
    .replace(/\/+$/, '');
  if (!isHttpsUrl(base)) return '';
  if (base.slice('https://'.length).includes('/')) return '';
  return `${base}/.well-known/jwks.json`;
};

/**
 * `/authorize`, `/token` and friends are endpoints, not issuers — the mistake
 * behind a keys URL that 404s. A path on its own is legitimate (Keycloak
 * realms, Azure AD tenants), so this drives a hint, never a blocked save.
 */
const ISSUER_ENDPOINT_PATH =
  /\/(authorize|authorization|login|signin|sign-in|token|userinfo)$/i;

export const issuerLooksLikeEndpoint = (issuer) =>
  ISSUER_ENDPOINT_PATH.test(
    String(issuer || '')
      .trim()
      .replace(/\/+$/, '')
  );

/**
 * Client-side mirror of the API's validation, so a typo is caught before a
 * round trip. Keyed by field name for the inline errors.
 */
export const validateOauthForm = (form = {}) => {
  const errors = {};
  const issuer = (form.issuer || '').trim();
  const audience = (form.audience || '').trim();
  const jwks = (form.jwks_uri || '').trim();
  const introspection = (form.introspection_endpoint || '').trim();

  if (!issuer) errors.issuer = 'Required — your login system’s address.';
  else if (!isHttpsUrl(issuer)) errors.issuer = 'Must start with https://';

  // Not optional, and not a formality: without an audience a token your
  // provider minted for a different service is a valid token here too.
  if (!audience) {
    errors.audience =
      'Required — without it, a sign-in meant for another of your services would be accepted here.';
  }

  // The API takes an introspection endpoint instead of JWKS for providers that
  // issue opaque tokens, and only blocks publishing when neither is set.
  if (!jwks && !introspection) {
    errors.jwks_uri = 'Required — or set an introspection endpoint under Advanced.';
  } else if (jwks && !isHttpsUrl(jwks)) {
    errors.jwks_uri = 'Must start with https://';
  }

  if (introspection && !isHttpsUrl(introspection)) {
    errors.introspection_endpoint = 'Must start with https://';
  }

  return errors;
};

/** Optional in the payload, and validated as https URLs when present. */
const OPTIONAL_OAUTH_FIELDS = ['jwks_uri', 'introspection_endpoint', 'client_id'];

/**
 * @param {object} [config] the server's stored `oauth_config`, so an untouched
 *   masked `client_secret` is left out instead of overwriting the real one with
 *   asterisks — and so clearing a field can be told apart from never setting it.
 */
export const buildOauthConfigBody = (form = {}, config) => {
  const initial = oauthConfigToForm(config);
  const body = {
    issuer: (form.issuer || '').trim(),
    audience: (form.audience || '').trim(),
    required_scopes: parseScopes(form.required_scopes),
  };
  // A blank optional field is omitted rather than sent as "" — the API
  // validates these as https URLs and an empty string is not one, which would
  // fail a create where the customer simply never opened Advanced. An empty
  // string is only sent to clear a value that really is stored.
  OPTIONAL_OAUTH_FIELDS.forEach((key) => {
    const value = (form[key] || '').trim();
    if (value || initial[key]) body[key] = value;
  });
  if (secretChanged(form.client_secret, initial.client_secret)) {
    body.client_secret = (form.client_secret || '').trim();
  }
  return body;
};

/** Dirty check for the block. A masked secret left alone is not a change. */
export const oauthFormChanged = (form = {}, config) => {
  const initial = oauthConfigToForm(config);
  const plain = ['issuer', 'audience', 'jwks_uri', 'introspection_endpoint', 'client_id'];
  if (plain.some((key) => (form[key] || '').trim() !== initial[key].trim())) return true;
  if (parseScopes(form.required_scopes).join(',') !== parseScopes(initial.required_scopes).join(',')) {
    return true;
  }
  return secretChanged(form.client_secret, initial.client_secret);
};

// ── Long-running requests (agent servers) ──────────────────────────────────
// Most AI apps stop waiting for a tool after about 60 seconds, so an agent that
// takes minutes always fails over MCP. With `defer_after_seconds` set, a slow
// call answers with a job ID instead and the app collects the result through
// the platform's own `get_job_status` tool. Agent servers only — the API
// rejects all three fields on API and knowledge servers with a 400.

export const supportsLongRunning = (server) => server?.server_type === 'agent';

/** Appears to apps automatically while deferral is on; never a stored tool. */
export const JOB_STATUS_TOOL = 'get_job_status';

export const DEFER_MIN_SECONDS = 10;
export const DEFER_MAX_SECONDS = 300;
export const DEFER_PREFILL_SECONDS = 45;
/** Where most apps give up — advice only, the API accepts up to the max. */
export const DEFER_WARN_SECONDS = 60;
export const POLL_MIN_SECONDS = 10;
export const POLL_MAX_SECONDS = 600;
/**
 * Only the floor is checked here. The ceiling is the platform limit from
 * server config, which can differ per environment — the API's 400 names it.
 */
export const RUNTIME_MIN_SECONDS = 60;

export const LONG_RUNNING_COPY = {
  sectionTitle: 'Long-running requests',
  toggleLabel: 'Answer slow requests with a job ID',
  toggleTooltip:
    'AI apps such as Claude, ChatGPT and Town stop waiting for an answer after about 60 seconds. When this is on and your agent takes longer than the time set here, the app immediately gets a job ID and collects the answer when it is ready. Keep this below 60 seconds; 45 works for most apps. Leave it off if this server is called by scripts or code instead of an AI app.',
  pollLabel: 'Suggest checking back after',
  pollTooltip:
    'How long the app is told to wait before checking again for a slow answer. Set it close to how long your agent usually takes; for example 30 if it usually needs about a minute. Only apps that schedule their own checks (such as Town) use it. Leave empty to use the default of 120 seconds.',
  pollHelp: 'Only apps that schedule their own checks, such as Town, use it.',
  pollPlaceholder: '120 (default)',
  runtimeLabel: 'Stop a background job after',
  runtimeTooltip:
    "The longest a slow request may keep running in the background before it is stopped and reported as failed. Set it to about twice your agent's usual time; for example 600 for an agent that usually takes 5 minutes. It can only be lower than the platform limit of 900 seconds. Leave empty to use 900.",
  runtimeHelp: 'Can only be lower than the platform limit of 900 seconds.',
  runtimePlaceholder: '900 (default)',
  slowWarning:
    'Most AI apps stop waiting at 60 seconds, so they may never receive the job ID. Use a value below 60.',
  savedOn:
    'Saved. Reconnect this server in the apps that use it so they see the new get_job_status tool.',
  savedOff: 'Saved. Requests already running in the background can still be collected.',
};

/** `null`, absent and 0 all mean "off" / "platform default" on read. */
const toSecondsField = (value) => (value ? String(value) : '');

export const longRunningToForm = (server) => ({
  enabled: !!server?.defer_after_seconds,
  defer_after_seconds: toSecondsField(server?.defer_after_seconds),
  poll_after_seconds: toSecondsField(server?.poll_after_seconds),
  max_runtime_seconds: toSecondsField(server?.max_runtime_seconds),
});

/** Empty → 0 (off / default); anything not a whole number → NaN. */
const parseSeconds = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 0;
  const num = Number(text);
  return Number.isInteger(num) ? num : NaN;
};

/**
 * Client-side mirror of the API's 422 ranges, so most never reach the server.
 * The advanced fields are hidden while the toggle is off, so they are only
 * checked while it is on — an error nobody can see would block the save.
 */
export const validateLongRunningForm = (form) => {
  const errors = {};
  if (!form?.enabled) return errors;

  const defer = parseSeconds(form.defer_after_seconds);
  if (!(defer >= DEFER_MIN_SECONDS && defer <= DEFER_MAX_SECONDS)) {
    errors.defer_after_seconds = `Use 0 (off) or ${DEFER_MIN_SECONDS}–${DEFER_MAX_SECONDS} seconds`;
  }

  const poll = parseSeconds(form.poll_after_seconds);
  if (poll !== 0 && !(poll >= POLL_MIN_SECONDS && poll <= POLL_MAX_SECONDS)) {
    errors.poll_after_seconds = `Use ${POLL_MIN_SECONDS}–${POLL_MAX_SECONDS} seconds, or leave empty`;
  }

  const runtime = parseSeconds(form.max_runtime_seconds);
  if (runtime !== 0 && !(runtime >= RUNTIME_MIN_SECONDS)) {
    errors.max_runtime_seconds = `Use at least ${RUNTIME_MIN_SECONDS} seconds, or leave empty`;
  }

  return errors;
};

/** The ≥ 60 s warning — shown beside the toggle, never blocks the save. */
export const deferLikelyTooSlow = (form) =>
  !!form?.enabled && parseSeconds(form.defer_after_seconds) >= DEFER_WARN_SECONDS;

/**
 * Only the fields that changed. 0 is how a value is cleared (it reads back as
 * `null`); leaving a field out leaves it as it is. Switching the toggle off
 * sends only `defer_after_seconds: 0` — the advanced values stay stored but
 * unused, and come back if it is switched on again.
 */
export const buildLongRunningBody = (form, server) => {
  const initial = longRunningToForm(server);
  const body = {};

  if (!form.enabled) {
    if (initial.enabled) body.defer_after_seconds = 0;
    return body;
  }

  ['defer_after_seconds', 'poll_after_seconds', 'max_runtime_seconds'].forEach((key) => {
    const next = parseSeconds(form[key]);
    if (next !== parseSeconds(initial[key])) body[key] = next;
  });
  return body;
};

export const longRunningChanged = (form, server) =>
  Object.keys(buildLongRunningBody(form, server)).length > 0;

// ── Status ─────────────────────────────────────────────────────────────────

export const isPublished = (server) =>
  server?.status === 'published' || server?.is_published === true;

export const getServerStatusMeta = (server) => {
  const status = server?.status || (server?.is_published ? 'published' : 'draft');
  switch (status) {
    case 'published':
      return { key: 'published', label: 'Published', tone: 'success' };
    case 'disabled':
      return { key: 'disabled', label: 'Disabled', tone: 'warning' };
    case 'draft':
    default:
      return { key: 'draft', label: 'Draft', tone: 'neutral' };
  }
};

// ── Tool curation ──────────────────────────────────────────────────────────

/**
 * Above roughly this many *enabled* tools, model tool-selection degrades in
 * both Claude and GPT and the client's context fills before the user types
 * anything. The API itself enables none of them past this count on import.
 */
export const TOOL_SOFT_LIMIT = 40;

export const isToolDestructive = (tool) => tool?.annotations?.destructiveHint === true;
export const isToolReadOnly = (tool) => tool?.annotations?.readOnlyHint === true;
export const isToolLowConfidence = (tool) => tool?.confidence === 'low';

/**
 * Badges a customer must see before exposing a tool to a model. Clients use the
 * annotations to gate write calls behind user confirmation, so "modifies data"
 * is the one that carries weight.
 */
export const getToolBadges = (tool) => {
  const badges = [];
  if (isToolDestructive(tool)) {
    badges.push({
      key: 'destructive',
      label: 'modifies data',
      tone: 'danger',
      title: 'This tool changes data upstream. Clients ask the user before calling it.',
    });
  }
  if (isToolReadOnly(tool)) {
    badges.push({
      key: 'readonly',
      label: 'read-only',
      tone: 'neutral',
      title: 'This tool only reads — it does not change anything upstream.',
    });
  }
  if (isToolLowConfidence(tool)) {
    badges.push({
      key: 'confidence',
      label: 'review before publishing',
      tone: 'warning',
      title: 'Schema was inferred from a single example (Postman / cURL import). Check the parameters.',
    });
  }
  return badges;
};

export const countEnabled = (tools = []) => tools.filter((t) => t?.enabled).length;

/** Duplicate tool names are a blocking lint finding — flag them inline too. */
export const findDuplicateToolNames = (tools = []) => {
  const seen = new Map();
  tools.forEach((t) => {
    const name = t?.tool_name;
    if (!name) return;
    seen.set(name, (seen.get(name) || 0) + 1);
  });
  return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([name]) => name));
};

export const TOOL_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Not exposed' },
  { value: 'readonly', label: 'Read-only' },
  { value: 'destructive', label: 'Modifies data' },
  { value: 'lowConfidence', label: 'Low confidence' },
];

export const filterTools = (tools = [], { search = '', filter = 'all' } = {}) => {
  const q = search.trim().toLowerCase();
  return tools.filter((tool) => {
    if (q) {
      const haystack = `${tool?.tool_name || ''} ${tool?.description || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    switch (filter) {
      case 'enabled':
        return !!tool?.enabled;
      case 'disabled':
        return !tool?.enabled;
      case 'readonly':
        return isToolReadOnly(tool);
      case 'destructive':
        return isToolDestructive(tool);
      case 'lowConfidence':
        return isToolLowConfidence(tool);
      case 'all':
      default:
        return true;
    }
  });
};

// ── Lint ───────────────────────────────────────────────────────────────────

/**
 * Findings may arrive as plain strings or as {code, message} objects — accept
 * both and give each one human copy where we recognise it, so a customer never
 * has to decode a raw check name.
 */
const LINT_COPY = [
  {
    match: /no tools enabled|tools_enabled|empty_tool/i,
    text: 'No tools are exposed yet — enable at least one on the Tools tab.',
  },
  {
    match: /base.?url/i,
    text: 'This server has no upstream base URL. Set it in Settings before publishing.',
  },
  {
    match: /private|internal|loopback|link.?local/i,
    text: "The upstream host resolves to a private address the server can't reach from outside your network.",
  },
  {
    match: /write.*(anonymous|none|unauthenticated)|anonymous.*write|auth.*write/i,
    text: 'Write tools are exposed on a server with no token. Anyone who finds the URL could change your data — switch to token auth, or disable the write tools.',
  },
  {
    match: /duplicate/i,
    text: 'Two tools share the same name. Rename one on the Tools tab.',
  },
];

/**
 * Findings the API already words for a human, matched first so the patterns
 * above can't rewrite them. Without this a JWKS fetch error that happens to
 * quote "internal server error" would be relabelled as a private-address
 * problem, sending the customer to look at the wrong thing entirely.
 */
const LINT_PASS_THROUGH = [
  /jwks/i,
  /audience/i,
  /introspection/i,
  /sign in with your own login/i,
  // Tool names and validator reasons inside it ("get_base_url", "duplicate
  // key") would otherwise trip the patterns above.
  /invalid input schema/i,
];

export const normalizeLintFindings = (findings) => {
  if (!Array.isArray(findings)) return [];
  return findings
    .map((f, index) => {
      const raw = typeof f === 'string' ? f : f?.message || f?.detail || f?.code || '';
      if (!raw) return null;
      const friendly = LINT_PASS_THROUGH.some((re) => re.test(raw))
        ? null
        : LINT_COPY.find((entry) => entry.match.test(raw));
      return {
        id: (typeof f === 'object' && f?.code) || `finding-${index}`,
        raw,
        message: friendly ? friendly.text : raw,
        // Keep the original around when we rewrote it, so nothing is hidden.
        detail: friendly && friendly.text !== raw ? raw : null,
      };
    })
    .filter(Boolean);
};

/**
 * `invalid_tools` names every enabled tool whose input schema clients that
 * validate schemas (OpenAI, the Python SDK) would reject — the blocking text
 * only names the first five, and its reasons can contain `;` and brackets, so
 * links are built from this list and never parsed out of that text. Older
 * backends don't send it; absent means none.
 */
const normalizeInvalidTools = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((t) => t && t.tool_id != null)
    .map((t) => ({ toolId: t.tool_id, toolName: t.tool_name || `Tool ${t.tool_id}`, reason: t.reason || '' }));

export const normalizeLint = (lint) => ({
  blocking: normalizeLintFindings(lint?.blocking),
  warnings: normalizeLintFindings(lint?.warnings),
  invalidTools: normalizeInvalidTools(lint?.invalid_tools),
});

export const EMPTY_LINT = { blocking: [], warnings: [], invalidTools: [] };

/** tool id → reason, for the "Invalid schema" badges on the Tools tab. */
export const invalidToolsById = (invalidTools = []) =>
  new Map(invalidTools.map((t) => [String(t.toolId), t.reason]));

// ── JSON-Schema → form fields (tool test panel) ────────────────────────────

/**
 * Flattens a tool's `input_schema` into a list of renderable field descriptors.
 * Nested objects and arrays fall back to a JSON textarea rather than trying to
 * generate a nested form — the customer is testing, not filling in a wizard.
 */
export const schemaToFields = (inputSchema) => {
  const properties = inputSchema?.properties;
  if (!properties || typeof properties !== 'object') return [];
  const required = Array.isArray(inputSchema.required) ? inputSchema.required : [];

  return Object.entries(properties).map(([name, spec = {}]) => {
    const type = Array.isArray(spec.type) ? spec.type.find((t) => t !== 'null') : spec.type;
    let kind = 'string';
    if (Array.isArray(spec.enum) && spec.enum.length) kind = 'enum';
    else if (type === 'boolean') kind = 'boolean';
    else if (type === 'integer' || type === 'number') kind = 'number';
    else if (type === 'object' || type === 'array') kind = 'json';

    return {
      name,
      kind,
      type: type || 'string',
      label: spec.title || name,
      description: spec.description || '',
      required: required.includes(name),
      options: Array.isArray(spec.enum) ? spec.enum : null,
      default: spec.default,
      isInteger: type === 'integer',
    };
  });
};

export const buildDefaultArgs = (fields = []) => {
  const values = {};
  fields.forEach((field) => {
    if (field.default !== undefined) {
      values[field.name] = field.kind === 'json' ? prettyJson(field.default) : field.default;
    } else if (field.kind === 'boolean') {
      values[field.name] = false;
    } else {
      values[field.name] = '';
    }
  });
  return values;
};

/**
 * Turns raw form values into the `arguments` payload, reporting per-field
 * errors rather than throwing — so the panel can highlight the bad field.
 * @returns {{args: object, errors: object}}
 */
export const coerceArgs = (fields = [], values = {}) => {
  const args = {};
  const errors = {};

  fields.forEach((field) => {
    const value = values[field.name];
    const isBlank = value === '' || value === undefined || value === null;

    if (isBlank && field.kind !== 'boolean') {
      if (field.required) errors[field.name] = 'Required';
      return; // omit optional blanks entirely rather than sending ""
    }

    switch (field.kind) {
      case 'boolean':
        args[field.name] = !!value;
        break;
      case 'number': {
        const num = Number(value);
        if (Number.isNaN(num)) {
          errors[field.name] = 'Must be a number';
        } else if (field.isInteger && !Number.isInteger(num)) {
          errors[field.name] = 'Must be a whole number';
        } else {
          args[field.name] = num;
        }
        break;
      }
      case 'json':
        try {
          args[field.name] = JSON.parse(value);
        } catch (e) {
          errors[field.name] = 'Not valid JSON';
        }
        break;
      default:
        args[field.name] = value;
    }
  });

  return { args, errors };
};

// ── Install snippets ───────────────────────────────────────────────────────

export const TOKEN_PLACEHOLDER = 'YOUR_TOKEN';

/**
 * The API returns the `YOUR_TOKEN` placeholder because it does not retain the
 * token. When we show snippets alongside a freshly minted token we substitute
 * the real value, so the customer copies something that actually works —
 * most connector setup failures are copy-paste errors, not server problems.
 */
export const substituteToken = (value, token) => {
  if (!token) return value;
  if (typeof value === 'string') return value.split(TOKEN_PLACEHOLDER).join(token);
  if (Array.isArray(value)) return value.map((item) => substituteToken(item, token));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, substituteToken(v, token)])
    );
  }
  return value;
};

/**
 * Ordered install targets. `note` is the thing a customer gets wrong if nobody
 * tells them — Claude's connector dialog in particular hides the header field.
 * `oauthNote` replaces it on a sign-in server, where there is no header to fill
 * in and the default note would send the user hunting for a token.
 */
export const INSTALL_TARGETS = [
  {
    key: 'claude_desktop_or_web',
    label: 'Claude web / desktop',
    note: 'Paste the URL into Add connector. The token goes in the connector dialog’s Request headers field as: Authorization: Bearer <token>',
    oauthNote: 'Paste the URL into Add connector, then click Connect and sign in. No headers to fill in.',
  },
  { key: 'claude_code', label: 'Claude Code', note: 'Run this in your terminal.' },
  { key: 'anthropic_api', label: 'Anthropic Messages API', note: 'The mcp_servers request parameter.' },
  { key: 'openai_responses_api', label: 'OpenAI Responses API', note: 'A tools entry of type "mcp".' },
  { key: 'cursor_or_vscode', label: 'Cursor / VS Code / Windsurf', note: 'Add to your mcp.json.' },
  { key: 'curl', label: 'curl', note: 'A raw JSON-RPC call, for checking connectivity.' },
];

/** Client support matrix (§10). ChatGPT is the one that must not be oversold. */
export const CLIENT_COMPATIBILITY = [
  { client: 'Claude web / desktop connectors', supported: true, note: 'Token goes in the connector dialog’s Request headers field.' },
  { client: 'Claude Code', supported: true, note: 'claude mcp add --transport http' },
  { client: 'Anthropic Messages API', supported: true, note: 'mcp_servers parameter.' },
  { client: 'OpenAI Responses API', supported: true, note: 'tools: [{ type: "mcp", ... }]' },
  { client: 'Cursor / VS Code / Windsurf', supported: true, note: 'mcp.json with url + headers.' },
  {
    client: 'ChatGPT connectors',
    supported: 'partial',
    note: 'Un-authenticated servers only. Authenticated connectors need OAuth 2.1 + Dynamic Client Registration, which is not built yet.',
    unavailableWhenTokenRequired:
      'Not available for this server — ChatGPT cannot authenticate to it. Options today are a no-auth server (public read-only data only) or waiting for OAuth.',
  },
];

export const prettyJson = (value) => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

/** Render any install-block value as copyable text. */
export const snippetToText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return prettyJson(value);
};

// ── Logs ───────────────────────────────────────────────────────────────────

const LOG_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'ok', label: 'OK' },
  { value: 'error', label: 'Error' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'rate_limited', label: 'Rate limited' },
  { value: 'deferred', label: 'Answered later', agentOnly: true },
  { value: 'cancelled', label: 'App stopped waiting' },
];

/**
 * Only agent servers can defer, so only they offer that filter. Any app can
 * stop waiting on any server, so `cancelled` stays everywhere.
 */
export const getLogStatusOptions = (server) =>
  LOG_STATUS_OPTIONS.filter((o) => !o.agentOnly || supportsLongRunning(server));

/**
 * Badge per status. `label` replaces the raw value only where the raw value
 * would mislead — `deferred` is not an error, `cancelled` is the app's doing.
 * A status this list doesn't know shows its raw value in amber, so a new one
 * from the API is visible rather than looking like a quiet success.
 *
 * `agentHint` points at the Settings toggle, so it is only added on agent
 * servers — anywhere else it would send the customer to a setting that isn't
 * there.
 */
const LOG_STATUS_META = {
  ok: { tone: 'success' },
  error: { tone: 'danger' },
  blocked: { tone: 'warning' },
  rate_limited: { tone: 'warning' },
  deferred: {
    tone: 'info',
    label: 'Answered later',
    title:
      "The agent took longer than the server's setting, so the app received a job ID and collected the answer in the background. Not an error.",
  },
  cancelled: {
    tone: 'warning',
    label: 'App stopped waiting',
    title: 'The app gave up before the tool finished, usually after about 60 seconds.',
    agentHint:
      'If this agent is often slow, turn on "Answer slow requests with a job ID" in Settings.',
  },
};

export const getLogStatusMeta = (status, server) => {
  const meta = LOG_STATUS_META[status];
  if (!meta) return { tone: 'warning', label: status || '—', title: null };
  const title =
    meta.agentHint && supportsLongRunning(server) ? `${meta.title} ${meta.agentHint}` : meta.title;
  return { tone: meta.tone, label: meta.label || status, title: title || null };
};

/** Default retention for the call log, per MCP_CALL_LOG_RETENTION_DAYS. */
export const LOG_RETENTION_DAYS = 30;

export const formatLogTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// ── Secrets ────────────────────────────────────────────────────────────────

/**
 * Secrets come back masked (`****1234`) everywhere. Sending a mask back would
 * overwrite the real credential with literal asterisks, so a field is only
 * submitted when the customer actually changed it.
 */
export const isMaskedSecret = (value) => typeof value === 'string' && /^\*{2,}/.test(value.trim());

export const secretChanged = (current, initial) =>
  typeof current === 'string' && current !== initial && !isMaskedSecret(current);

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * Field validation arrives as `detail: [{loc, msg, type}]`. Returning that
 * array straight to a component renders an object as a React child, which
 * throws — so it is flattened to one sentence naming the fields. Nested bodies
 * like `oauth_config` make this the ordinary shape of a rejected save.
 */
const describeValidationDetail = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        const message = entry?.msg || entry?.message;
        if (!message) return null;
        const field = Array.isArray(entry?.loc) ? entry.loc[entry.loc.length - 1] : null;
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    return parts.length ? parts.join('. ') : null;
  }
  if (detail && typeof detail === 'object') return detail.msg || detail.message || null;
  return null;
};

/**
 * The axios interceptor already normalises errors, but import and enrich run
 * long enough that a client timeout is a real possibility — and a timeout does
 * NOT mean the work failed, so it must not read like it did.
 */
export const describeError = (error, fallback = 'Something went wrong.') => {
  if (!error) return fallback;
  if (error.isTimeout) {
    return 'This is taking longer than expected and may still be running on the server. Refresh in a moment to see the result.';
  }
  if (error.isNetworkError) return "Couldn't reach the server. Check your connection and try again.";
  return (
    error.data?.error ||
    describeValidationDetail(error.data?.detail) ||
    error.message ||
    fallback
  );
};

export const isNotFound = (error) => error?.status === 404;
